import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Users, MessageSquare, FileText, LayoutDashboard, ShieldCheck, Scale, Briefcase } from "lucide-react";
import { adminMe, adminLogout, getToken } from "../../lib/adminApi";

/**
 * Auth-gated layout for /admin/*. Renders sidebar + outlet for child pages.
 * Redirects to /admin/login if no valid token.
 */
export default function AdminLayout() {
    const navigate = useNavigate();
    const [authState, setAuthState] = useState({ status: "checking", user: null });

    useEffect(() => {
        document.title = "Admin — Cleany";
        if (!getToken()) {
            setAuthState({ status: "unauth", user: null });
            return;
        }
        adminMe()
            .then((u) => setAuthState({ status: "auth", user: u }))
            .catch(() => setAuthState({ status: "unauth", user: null }));
    }, []);

    if (authState.status === "unauth") {
        return <Navigate to="/admin/login" replace />;
    }
    if (authState.status === "checking") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500" data-testid="admin-loading">
                Henter…
            </div>
        );
    }

    const handleLogout = async () => {
        await adminLogout();
        navigate("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <aside className="fixed inset-y-0 left-0 w-64 bg-slate-900 text-slate-100 flex flex-col" data-testid="admin-sidebar">
                <div className="px-6 py-7 border-b border-slate-800">
                    <Link to="/admin" className="flex items-center gap-2 text-white font-display font-bold text-lg">
                        <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-sky-500 to-sky-700 inline-flex items-center justify-center">
                            <ShieldCheck className="h-4 w-4" />
                        </span>
                        Cleany Admin
                    </Link>
                </div>
                <nav className="flex-1 px-3 py-5 space-y-1">
                    <NavItem to="/admin" icon={LayoutDashboard} label="Oversigt" end testid="nav-dashboard" />
                    <NavItem to="/admin/waitlist" icon={Users} label="Waitlist" testid="nav-waitlist" />
                    <NavItem to="/admin/contacts" icon={MessageSquare} label="Kontakt" testid="nav-contacts" />
                    <NavItem to="/admin/posts" icon={FileText} label="Updates" testid="nav-posts" />
                    <NavItem to="/admin/legal/dpa" icon={Scale} label="Databehandleraftale" testid="nav-legal-dpa" />
                    <div className="pt-3 mt-3 border-t border-slate-800/70">
                        <NavLink
                            to="/crm"
                            data-testid="nav-crm"
                            className="flex items-center justify-between gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-300 hover:bg-sky-600 hover:text-white transition-colors"
                        >
                            <span className="flex items-center gap-2.5">
                                <Briefcase className="h-4 w-4" />
                                CRM
                            </span>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-400">Åbn</span>
                        </NavLink>
                    </div>
                </nav>
                <div className="p-4 border-t border-slate-800">
                    <div className="text-xs text-slate-400 mb-2">Logget ind som</div>
                    <div className="text-sm font-semibold text-white truncate">{authState.user?.email}</div>
                    <button
                        type="button"
                        onClick={handleLogout}
                        data-testid="admin-logout-btn"
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-medium h-9 transition-colors"
                    >
                        <LogOut className="h-4 w-4" />
                        Log ud
                    </button>
                </div>
            </aside>
            <main className="ml-64 min-h-screen">
                <div className="max-w-6xl mx-auto p-8">
                    <Outlet context={{ user: authState.user }} />
                </div>
            </main>
        </div>
    );
}

function NavItem({ to, icon: Icon, label, end, testid }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
                "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors " +
                (isActive
                    ? "bg-slate-800 text-white"
                    : "text-slate-300 hover:bg-slate-800/60 hover:text-white")
            }
        >
            <Icon className="h-4 w-4" />
            {label}
        </NavLink>
    );
}
